import {
  Button,
  Col,
  Collapse,
  Descriptions,
  Divider,
  Drawer,
  Image,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore"
import dayjs from "dayjs"
import { db } from "../../../firebase/config"
import EditEntry from "./EditEntry"
import { DollarOutlined } from "@ant-design/icons"

const { Text, Link: TypographyLink } = Typography
const { Panel } = Collapse

/**
 * A drawer component that displays the details of a saved price calculation.
 * It includes a sub-drawer to view the historical versions of the price.
 * @param {object} data - The price calculation object to display.
 * @param {boolean} fromAdmin - A flag indicating if the view is from an admin context.
 */
export default function ShowSavedPricesDrawer({ data, fromAdmin, triggerElement }) {
  // State for the main drawer visibility
  const [open, setOpen] = useState(false)

  // State for the history sub-drawer
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false)
  const [priceHistory, setPriceHistory] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)

  const [productType, setProductType] = useState(data?.productType || null)
  const [options, setOptions] = useState([])
  const [loadingOptions, setLoadingOptions] = useState(true)
  const [savingType, setSavingType] = useState(false)

  useEffect(() => {
    const loadProductTypes = async () => {
      setLoadingOptions(true)
      try {
        const snap = await getDoc(doc(db, "settings", "productTypeList"))
        if (!snap.exists()) throw new Error("Settings document 'productTypeList' not found.")

        const arr = snap.data()?.productTypes ?? []
        if (!Array.isArray(arr) || arr.length === 0) throw new Error("No product types found.")

        setOptions(arr.map((s) => ({ value: s, label: s })))
      } catch (err) {
        console.error("Failed to load product types:", err)
      } finally {
        setLoadingOptions(false)
      }
    }

    loadProductTypes()
  }, [])

  const handleProductTypeChange = async (value) => {
    setProductType(value)
    setSavingType(true)
    try {
      await updateDoc(doc(db, "priceCalculation", data.id), {
        productType: value,
      })
      message.success("Product type updated.")
    } catch (err) {
      console.error("Error updating product type:", err)
      message.error("Failed to update product type.")
    } finally {
      setSavingType(false)
    }
  }

  /**
   * Fetches and displays the price history for the current item.
   */
  const fetchPriceHistory = async () => {
    setHistoryLoading(true)
    setHistoryDrawerOpen(true)

    try {
      // 1. Query the history collection WITHOUT ordering it on the database.
      const historyRef = collection(db, "priceCalculationHistory")
      const q = query(
        historyRef,
        where("originalPriceId", "==", data.id),
        // The orderBy clause has been removed from here.
      )

      const querySnapshot = await getDocs(q)
      let historyData = querySnapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }))

      // 2. Sort the results here in the frontend using JavaScript.
      // We sort by the 'seconds' property of the Firestore Timestamp.
      // (b - a) gives us descending order (newest first).
      historyData.sort((a, b) => b.archivedAt.seconds - a.archivedAt.seconds)

      setPriceHistory(historyData)
    } catch (error) {
      console.error("Error fetching price history:", error)
      message.error("Could not fetch price history.")
    } finally {
      setHistoryLoading(false)
    }
  }

  // Handlers for opening and closing drawers
  const showDrawer = () => setOpen(true)
  const onClose = () => setOpen(false)
  const onHistoryClose = () => {
    setHistoryDrawerOpen(false)
    // Optional: clear history data when closing to refetch next time
    setPriceHistory([])
  }

  /**
   * Deletes the current price calculation document from Firestore.
   * @param {string} id - The ID of the document to delete.
   */
  const handleDeletePrice = async (id) => {
    if (!id) {
      message.error("Invalid price ID")
      return
    }
    const priceRef = doc(db, "priceCalculation", id)
    try {
      await deleteDoc(priceRef)
      message.success("Price removed!")
      onClose() // Close the drawer after deletion
    } catch (err) {
      message.error(err.message)
    }
  }

  /**
   * A reusable function to render a list of dynamic or fixed prices.
   * @param {Array} prices - The array of dynamic prices.
   * @param {Array} fixedPrices - The array of fixed prices.
   * @param {boolean} fixed - A flag to determine which price list to show.
   * @returns {JSX.Element|null}
   */
  const returnPrices = (prices, fixedPrices, fixed) => {
    const dynamicPriceCopy = Array.isArray(prices) ? [...prices] : []
    const fixedDynamicPriceCopy = Array.isArray(fixedPrices) ? [...fixedPrices] : []

    // This logic seems specific to the main data, you may want to adjust
    // if it shouldn't apply to historical data. For now, it's kept as is.
    if (data.labelCount > 0) {
      const newPriceObject = { price: data.price, quantity: data.labelCount }
      dynamicPriceCopy.push(newPriceObject)
      fixedDynamicPriceCopy.push(newPriceObject)
    }
    if (data.totalFixedPrice && fixed) {
      fixedDynamicPriceCopy.push({ price: data.totalFixedPrice, quantity: 1000 })
    }

    if (fixed && fixedDynamicPriceCopy.length > 0) {
      return (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Divider>
            <Tag color="red">Fixed Prices</Tag>
          </Divider>
          {fixedDynamicPriceCopy.map((item, index) => (
            <Descriptions
              bordered
              size="small"
              column={2}
              key={`${item.quantity}-${item.price}-${index}`}>
              <Descriptions.Item label="Quantity">
                {item.quantity?.toLocaleString("fr-FR")}
              </Descriptions.Item>
              <Descriptions.Item label="Price">{item.price}</Descriptions.Item>
            </Descriptions>
          ))}
        </Space>
      )
    } else if (!fixed && dynamicPriceCopy.length > 0) {
      return (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Divider>
            <Tag color="blue">Dynamic Prices</Tag>
          </Divider>
          {dynamicPriceCopy.map((item, index) => (
            <Descriptions
              bordered
              size="small"
              column={2}
              key={`${item.quantity}-${item.price}-${index}`}>
              <Descriptions.Item label="Quantity">
                {item.quantity?.toLocaleString("fr-FR")}
              </Descriptions.Item>
              <Descriptions.Item label="Price">{item.price}</Descriptions.Item>
            </Descriptions>
          ))}
        </Space>
      )
    }
    return <Text type="secondary">No price list available.</Text>
  }

  // Data to pass to the calculation page for recalculation
  const updatedForState = {
    ...data,
    recalculation: true,
    fromAdmin: fromAdmin,
  }

  return (
    <>
      {triggerElement ? (
        <span onClick={showDrawer} style={{ cursor: "pointer", display: "inline-flex" }}>
          {triggerElement}
        </span>
      ) : (
        <TypographyLink onClick={showDrawer}>
          <DollarOutlined /> Price Details
        </TypographyLink>
      )}

      {/* Main Drawer for Current Price Details */}
      <Drawer
        title={`Price : ${data.product.productName}`}
        placement="right"
        onClose={onClose}
        width={450}
        open={open}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Space wrap>
              <Button danger onClick={() => handleDeletePrice(data.id)}>
                Delete Price
              </Button>
              <Link state={updatedForState} to={`/calculations/`}>
                <Button>Re-Calculate</Button>
              </Link>
              <Button type="dashed" onClick={fetchPriceHistory}>
                Show price history
              </Button>
            </Space>
          </Col>
          <Col span={24}>
            <Image
              width="100%"
              height={150}
              style={{ objectFit: "contain" }}
              src={data.product.productFileUrl || "https://via.placeholder.com/150"}
            />
          </Col>
        </Row>

        <Divider>Main Info</Divider>
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="Client">{data.client}</Descriptions.Item>
          <Descriptions.Item label="Product">
            <EditEntry data={data} text={data?.product?.productName} />
          </Descriptions.Item>
          <Descriptions.Item label="Price ID">
            <Text copyable={{ text: data.id }}>{data.id}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Product Type">
            <Select
              style={{ width: "100%" }}
              placeholder="Select product type"
              loading={loadingOptions || savingType}
              value={productType}
              onChange={handleProductTypeChange}
              options={options}
              disabled={loadingOptions || savingType}
            />
          </Descriptions.Item>
        </Descriptions>

        <Divider>Product</Divider>
        <Descriptions size="small" column={2} bordered>
          <Descriptions.Item label="Width">{data.labelWidth}mm</Descriptions.Item>
          <Descriptions.Item label="Height">{data.labelHeight}mm</Descriptions.Item>
          <Descriptions.Item label="Machine">{data.machine}</Descriptions.Item>
          <Descriptions.Item label="Material">{data.material}</Descriptions.Item>
          <Descriptions.Item label="Knife" span={2}>
            {data.knifeCode}
          </Descriptions.Item>
        </Descriptions>

        <Divider>Print</Divider>
        <Descriptions size="small" column={2} bordered>
          <Descriptions.Item label="CMYK">{data.print.cmykColors}</Descriptions.Item>
          <Descriptions.Item label="Pantone">{data.print.pantoneColors}</Descriptions.Item>
          <Descriptions.Item label="White">{data.print.coveringWCount}</Descriptions.Item>
          <Descriptions.Item label="Gold/Silver">{data.print.goldOrSilver}</Descriptions.Item>
          <Descriptions.Item label="Varnish">{data.print.varnishType}</Descriptions.Item>
          <Descriptions.Item label="Foil Count">{data.print.foilCount}</Descriptions.Item>
        </Descriptions>

        {returnPrices(data.dynamicPrice, data.dynamicFixedPrice, data.advanced.fixedPrice)}
      </Drawer>

      {/* History Sub-Drawer */}
      <Drawer
        title={`History for: ${data.product.productName}`}
        placement="right"
        onClose={onHistoryClose}
        open={historyDrawerOpen}
        width={420}>
        {historyLoading ? (
          <div style={{ textAlign: "center", marginTop: 40 }}>
            <Spin tip="Loading History..." size="large" />
          </div>
        ) : priceHistory.length > 0 ? (
          <Collapse accordion>
            {priceHistory.map((historyItem) => (
              <Panel
                header={`Changed: ${dayjs
                  .unix(historyItem.archivedAt.seconds)
                  .format("DD.MM.YYYY HH:mm")}`}
                key={historyItem.id}
                extra={<Text type="secondary">by {historyItem.archivedBy?.email || "N/A"}</Text>}>
                {returnPrices(
                  historyItem.dynamicPrice,
                  historyItem.dynamicFixedPrice,
                  historyItem.advanced.fixedPrice,
                )}
              </Panel>
            ))}
          </Collapse>
        ) : (
          <Text type="secondary">No price history found for this item.</Text>
        )}
      </Drawer>
    </>
  )
}
